import {ThemeProvider} from '@primer/react-brand'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {ContentfulSubnav} from '@github-ui/swp-core/components/contentful/ContentfulSubnav'
import {useContentfulData} from '@github-ui/swp-core/lib/hooks/use-contentful-data'

import {toPayload} from '../../../lib/types/payload'
import {toBrandPage, type GenericSection} from '../../../brand/lib/types/contentful'

import HeroSection from './sections/Hero/Hero'
import FeaturesSection from './sections/Features/Features'
import PricingSection from './sections/Pricing/Pricing'
import ResourcesSection from './sections/Resources/Resources'
import FaqSection from './sections/Faq/Faq'
import {
  sanitizeFeaturesSectionData,
  sanitizeHeroSectionData,
  sanitizePricingSectionData,
  sanitizeResourcesSectionData,
} from './utils/contentful'

export default function SecurityAdvancedSecurityIndex() {
  const {contentfulRawJsonResponse} = toPayload(useRoutePayload<unknown>())
  const contentfulResponse = useContentfulData(contentfulRawJsonResponse)
  const page = toBrandPage(contentfulResponse)

  const {template} = page.fields
  const {subnav} = template.fields
  const {
    securityAdvancedSecurityHeroSection: heroSection,
    securityAdvancedSecurityFeaturesSection: featuresSection,
    securityAdvancedSecurityPricingSection: pricingSection,
    securityAdvancedSecurityResourcesSection: resourcesSection,
    securityAdvancedSecurityFaqSection: faqSection,
  } = page.ids

  const heroSectionData = sanitizeHeroSectionData(heroSection as GenericSection)
  const featuresSectionData = sanitizeFeaturesSectionData(featuresSection as GenericSection)
  const pricingSectionData = sanitizePricingSectionData(pricingSection as GenericSection)
  const resourcesSectionData = sanitizeResourcesSectionData(resourcesSection as GenericSection)
  const faqGroupComponent = faqSection?.fields.content?.find(
    item => item.sys.contentType.sys.id === 'primerComponentFaqGroup',
  )

  return (
    <ThemeProvider colorMode="dark" className="lp-Security lp-has-fontSmoothing lp-textWrap--pretty">
      {subnav ? <ContentfulSubnav component={subnav} linkVariant="default" /> : null}

      {heroSectionData.intro && heroSectionData.bentos.length && heroSectionData.logoSuiteComponent ? (
        <HeroSection
          intro={heroSectionData.intro}
          tertiaryCta={heroSectionData.tertiaryCta}
          bentos={heroSectionData.bentos}
          logoSuiteComponent={heroSectionData.logoSuiteComponent}
        />
      ) : null}

      {featuresSectionData?.testimonial && featuresSectionData.bentos.length ? (
        <FeaturesSection {...featuresSectionData} />
      ) : null}

      {pricingSectionData?.intro && pricingSectionData?.plans.length ? (
        <PricingSection {...pricingSectionData} />
      ) : null}

      {resourcesSectionData?.title && resourcesSectionData?.cards.length ? (
        <ResourcesSection {...resourcesSectionData} />
      ) : null}

      {faqGroupComponent ? <FaqSection contentfulContent={faqGroupComponent} /> : null}
    </ThemeProvider>
  )
}
